import React, { useState, useEffect } from 'react';
import TenantLayout from '../components/TenantLayout';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileClock, Activity } from 'lucide-react';

export default function AuditLogs() {
    const { clientSlug } = useParams();
    const { api } = useAuth();
    
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchLogs = async () => {
        try {
            const res = await api.get(`/api/tenant/${clientSlug}/audit`);
            setLogs(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load audit logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [clientSlug]);

    return (
        <TenantLayout title="Audit Logs" tagline="Chronological trail of user actions for accountability">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <h2 className="text-lg font-bold text-[#0F284E] flex items-center gap-2">
                        <FileClock size={20} className="text-[#EAB308]" />
                        Recent Activities
                    </h2>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-200/50 px-2 py-1 rounded">Latest 100 entries</span>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left whitespace-nowrap min-w-[700px]">
                        <thead className="bg-[#0A192F] text-white text-xs uppercase font-mono tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Timestamp</th>
                                <th className="px-6 py-4">User</th>
                                <th className="px-6 py-4">Action</th>
                                <th className="px-6 py-4">Module</th>
                                <th className="px-6 py-4 min-w-[300px]">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                        <div className="flex justify-center mb-2"><Activity className="animate-pulse text-[#EAB308]" /></div>
                                        Loading audit trail...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">No audit logs found for this tenant.</td>
                                </tr>
                            ) : logs.map((log) => (
                                <tr key={log._id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-slate-500 text-xs font-mono">
                                        {new Date(log.timestamp).toLocaleString('en-IN', {
                                            year: 'numeric', month: 'short', day: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                        })}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-[#0F284E] font-medium">
                                        {log.user_id}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                            log.action === 'create' ? 'bg-emerald-100 text-emerald-700' :
                                            log.action === 'email_invoice' ? 'bg-indigo-100 text-indigo-700' :
                                            'bg-slate-100 text-slate-700'
                                        }`}>
                                            {log.action}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                                        {log.target_collection}
                                    </td>
                                    <td className="px-6 py-4 text-slate-700 text-sm">
                                        {log.details}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </TenantLayout>
    );
}
